import {
  Box,
  Grid, Stack,
  Typography
} from "@mui/material";
import {
  useTheme
} from "@mui/material/styles";
import { ChevronRight } from "lucide-react";
import {
  useEffect,
  useRef
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import {
  StyledGridUsers
} from "./CardRanking.styles";
import { CardRankingProps } from "./CardRanking.types";
import { CardRankingRow } from "./CardRankingRow.component";
import {
  Spinner
} from "../../components";
import { ButtonDigited, StyledTypography } from "../../components/Button/Button.variants";
import { useIntersectionObserver } from "../../hooks";
import { useLeaderboardQuery } from "../../queries/widgets";
import { useAuthStore, useLeaderboardClusterStore } from "../../stores";
import type { LeaderboardItem } from "../../types";

const blankStudent = {
  profilePic: null,
  studentName: "-------"
} as const;

export function CardRankingContainer({
  isUserPage=false,
  enabled=true
}: CardRankingProps)  {

  const { t } = useTranslation();
  const studentsContainerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const { pathname } = useLocation();

  const leaderboardConfiguration = useLeaderboardClusterStore(state => ({ leaderboardAnonymous:state.currentCluster?.anonymize, leaderboard: !!state.currentCluster }));
  const profileImage = useAuthStore(state => state.profileImage);

  const {
    data: studentPages,
    hasNextPage: hasMoreStudents,
    fetchNextPage: fetchMoreStudents,
    isError: isErrorStudents,
    isFetchingNextPage: isFetchingMoreStudents,
    isInitialLoading: isLoadingStudents,
    refetch
  } = useLeaderboardQuery({
    pageSize: 20,
    enabled
  });

  // refetch useLeaderboardQuery when profileImage changes
  useEffect(() => {
    refetch();
  }, [profileImage]);

  const intersectionObserver = useIntersectionObserver({
    callback: () => { hasMoreStudents && !isFetchingMoreStudents && fetchMoreStudents() },
    container: studentsContainerRef
  });

  const students = studentPages?.pages
    .reduce((acc, page) => {
      return acc.concat(page?.leaderboards);
    }, [] as LeaderboardItem[]) ?? [];

  const filteredStudents =
    (leaderboardConfiguration?.leaderboardAnonymous === true)
      ? students?.map((student) =>
        student?.isCaller ? student : Object?.assign(student, blankStudent)
      )
      : students;

  if(!leaderboardConfiguration?.leaderboard) return null;

  return (
    <>
      <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } gap={ "10px" } sx={ {
        paddingLeft: "16px",
        paddingRight: "16px"
      } }>
        <Grid aria-hidden={ "true" } container alignItems={ "center" } alignSelf={ "stretch" }
          justifyContent={ "space-between" }>
          <Grid item xs={ 3 }>
            <Typography
              fontWeight={ 500 }
              fontSize="0.75rem"
              color={ theme.customColors.systemSecondary01 }
              textAlign={ "start" }
            >
              { t("ranking_position") }
            </Typography>
          </Grid>
          <Grid item xs={ 6 }>
            <Typography
              fontWeight={ 500 }
              fontSize="0.75rem"
              color={ theme.customColors.systemSecondary01 }
            >
              { t("ranking_person") }
            </Typography>
          </Grid>
          <Grid item xs={ 3 }>
            <Typography
              fontWeight={ 500 }
              fontSize="0.75rem"
              color={ theme.customColors.systemSecondary01 }
              textAlign={ "end" }
            >
              { t("ranking_score") }
            </Typography>
          </Grid>
        </Grid>
        {
          (isLoadingStudents) ? (
            <Spinner />
          ) : null
        }
        <Stack direction={ "column" } alignItems={ "center" } justifyContent={ "center" } width="100%">
          {
            (filteredStudents.length > 0) ? (
              <StyledGridUsers
                role="list"
                aria-label={ t("student_list") }
                $profilePage={ isUserPage }
                ref={ studentsContainerRef }
              >
                {
                  studentPages?.pages?.[0]?.callerRanking && (
                    <Box sx={ {
                      width: "100%",
                      marginBottom: "16px"
                    } }>
                      <CardRankingRow
                        observer={ intersectionObserver }
                        observeStudent={ false }
                        student={ studentPages.pages[0].callerRanking }
                      />
                    </Box>
                  )
                }
                { Boolean(intersectionObserver) && filteredStudents.map((user, idx)=> (
                  <CardRankingRow
                    key={ `student--${user.studentEid}` }
                    observer={ intersectionObserver }
                    observeStudent={ (idx + 1) === (filteredStudents.length - 2) }
                    student={ user }
                  />
                )) }
              </StyledGridUsers>
            ) : null
          }
        </Stack>
        { (isErrorStudents && filteredStudents.length === 0) ? (
          <Typography>
            { t("ranking_error") }
          </Typography>
        ) : (!isLoadingStudents && filteredStudents.length === 0) ? (<Typography>
          { t("ranking_no_content") }
        </Typography>) : null }
      </Stack>
      { !pathname.includes("/profile") && (
        <ButtonDigited 
          href="/profile" 
          aria-label={ t("ranking_view_scoreboard") }
          typology="secondary" 
          buttonSize="sm" 
          selfPosition="flex-end" 
          sx={ { marginRight: "1rem" } }
        >
          <>
            <StyledTypography variant="h4"> { t("ranking_view_scoreboard") + " " } </StyledTypography> <ChevronRight size="0.875rem" />
          </>
        </ButtonDigited>
      ) }
    </>
  );
}

